const VOICE_COOLDOWN_MS = 1000;
const VOICE_REPEAT_BLOCK_MS = 2200;

const LIGHTING_LOW_WAV = '/face_enrolment_voices/lighting_low.wav';

const GUIDANCE_VOICE_CONFIG = {
  'face not detected': {
    wav: '/face_enrolment_voices/focus_on_camera.wav',
    speech: 'Face not detected.',
  },
  'multiple faces detected': {
    wav: '/face_enrolment_voices/only_one_face.wav',
    speech: 'Only one face please.',
  },
  'look straight': {
    wav: '/face_enrolment_voices/focus_on_camera.wav',
    speech: 'Please look straight.',
  },
  'turn left': {
    wav: '/face_enrolment_voices/turn_left.wav',
    speech: 'Please turn left.',
  },
  'turn right': {
    wav: '/face_enrolment_voices/turn_right.wav',
    speech: 'Please turn right.',
  },
  'move closer': {
    wav: '/face_enrolment_voices/move_closer.wav',
    speech: 'Move closer to the camera.',
  },
  'move back slightly': {
    speech: 'Move back slightly from the camera.',
  },
  'center your face': {
    wav: '/face_enrolment_voices/focus_on_camera.wav',
    speech: 'Please center your face in the frame.',
  },
  'tilt head down': {
    wav: '/face_enrolment_voices/tilt_head_down.wav',
    speech: 'Tilt your head down slightly.',
  },
  'hold still': {
    wav: '/face_enrolment_voices/hold_still.wav',
    speech: 'Hold still.',
  },
};

const BAD_LIGHTING_GUIDANCE = new Set(['lighting too low', 'too bright']);

let lastVoiceKey = '';
let lastVoiceAt = 0;
let activeAudio = null;
let activeSpeechTimeout = null;

const normalizeGuidanceKey = (guidance) => {
  if (!guidance) return null;
  return String(guidance).trim().toLowerCase();
};

export const getVoiceKeyFromGuidance = (guidance) => normalizeGuidanceKey(guidance);

const stopActiveVoice = () => {
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio = null;
  }
  if (typeof window !== 'undefined' && window.speechSynthesis) {
    window.speechSynthesis.cancel();
  }
  if (activeSpeechTimeout) {
    clearTimeout(activeSpeechTimeout);
    activeSpeechTimeout = null;
  }
};

const speakWithSynthesis = (text) => {
  if (typeof window === 'undefined' || !window.speechSynthesis || !text) return;

  const utterance = new SpeechSynthesisUtterance(text);
  utterance.rate = 1;
  utterance.pitch = 1;
  utterance.volume = 1;
  window.speechSynthesis.cancel();
  window.speechSynthesis.speak(utterance);
};

const playWav = (source) =>
  new Promise((resolve, reject) => {
    const audio = new Audio(source);
    audio.preload = 'auto';
    audio.onended = () => resolve();
    audio.onerror = () => reject(new Error('wav playback failed'));
    activeAudio = audio;
    audio.play().catch(reject);
  });

/**
 * Play enrollment voice for a guidance string (wav preferred, SpeechSynthesis fallback).
 */
/** Play lighting_low.wav only — used when lighting validation fails. */
export const playLightingLowVoice = async (options = {}) => {
  const { force = false, allowRepeat = false } = options;
  const voiceKey = 'lighting too low';
  if (typeof window === 'undefined') return;

  const now = Date.now();
  const isSameAsLast = voiceKey === lastVoiceKey;

  if (!force) {
    if (now - lastVoiceAt < VOICE_COOLDOWN_MS) return;
    if (isSameAsLast && now - lastVoiceAt < VOICE_REPEAT_BLOCK_MS && !allowRepeat) return;
    if (activeAudio && !activeAudio.paused) return;
  }

  stopActiveVoice();

  try {
    await playWav(LIGHTING_LOW_WAV);
    lastVoiceKey = voiceKey;
    lastVoiceAt = now;
  } catch (error) {
    console.error('Lighting low voice playback failed:', error);
  } finally {
    if (activeAudio) {
      activeAudio = null;
    }
  }
};

export const shouldPlayVoiceForGuidance = (guidance) => {
  const voiceKey = normalizeGuidanceKey(guidance);
  if (!voiceKey || voiceKey === 'lighting good' || voiceKey === 'checking lighting') {
    return false;
  }
  return true;
};

export const playEnrollmentVoice = async (guidance, options = {}) => {
  const { force = false, allowRepeat = false } = options;
  const voiceKey = normalizeGuidanceKey(guidance);
  if (!voiceKey || typeof window === 'undefined') return;

  if (BAD_LIGHTING_GUIDANCE.has(voiceKey)) {
    await playLightingLowVoice({ force, allowRepeat });
    return;
  }

  if (voiceKey === 'lighting good' || voiceKey === 'checking lighting') {
    return;
  }

  const config = GUIDANCE_VOICE_CONFIG[voiceKey];
  if (!config) return;

  const now = Date.now();
  const isSameAsLast = voiceKey === lastVoiceKey;

  if (!force) {
    if (now - lastVoiceAt < VOICE_COOLDOWN_MS) return;
    if (isSameAsLast && now - lastVoiceAt < VOICE_REPEAT_BLOCK_MS && !allowRepeat) return;
    if (activeAudio && !activeAudio.paused) return;
  }

  stopActiveVoice();

  try {
    if (config.wav) {
      await playWav(config.wav);
    } else if (config.speech) {
      speakWithSynthesis(config.speech);
    }
    lastVoiceKey = voiceKey;
    lastVoiceAt = now;
  } catch (error) {
    if (config.speech) {
      speakWithSynthesis(config.speech);
      lastVoiceKey = voiceKey;
      lastVoiceAt = now;
    } else {
      console.error('Enrollment voice playback failed:', error);
    }
  } finally {
    if (activeAudio) {
      activeAudio = null;
    }
  }
};

export const resetEnrollmentVoice = () => {
  stopActiveVoice();
  lastVoiceKey = '';
  lastVoiceAt = 0;
};
